import { useState, useEffect, useRef } from 'react';
import { FRIEND_PREFETCH_LIMIT, SPORTS } from '../data/constants';
import { calcStreak } from '../utils/streak';

const toDateStr = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function weekCount(records) {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const dow = today.getDay();
    const weekStart = new Date(today); weekStart.setDate(today.getDate() - dow);
    return records.filter(r => {
        const rd = new Date(r.date); rd.setHours(0, 0, 0, 0);
        return rd >= weekStart && rd <= today;
    }).length;
}



function FriendProfile({ friend, onBack, onRemove, getFriendData, showToast }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [confirmRemove, setConfirmRemove] = useState(false);
    const [removing, setRemoving] = useState(false);

    useEffect(() => {
        getFriendData(friend.uid)
            .then(d => { setData(d); setLoadError(!d); setLoading(false); })
            .catch(() => { setLoadError(true); setLoading(false); });
    }, [friend.uid]); // eslint-disable-line react-hooks/exhaustive-deps

    const records = data?.records || [];
    const weights = data?.weights || [];
    const latestWeight = [...weights].sort((a, b) => new Date(b.date) - new Date(a.date))[0];
    const recentRecs = [...records].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5);
    const streak = calcStreak(records);
    const totalMin = records.reduce((a, r) => a + (+r.duration || 0), 0);

    const handleRemove = () => setConfirmRemove(true);

    return (
        <>
            <button className="back-btn" onClick={onBack}>← 친구 목록</button>

            <div className="export-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 4 }}>{friend.nickname}</div>
                        <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'DM Mono,monospace' }}>@{friend.userId}</div>
                    </div>
                    <button className="tpl-btn del" style={{ flex: 0, padding: '6px 12px' }} onClick={handleRemove}>삭제</button>
                </div>
            </div>

            {loading ? (
                <div className="empty-state"><div className="empty-text">···</div></div>
            ) : loadError ? (
                <div className="empty-state">
                    <div className="empty-text">친구 기록을 불러올 수 없어요.<br />공개 권한이나 네트워크 상태를 확인해 주세요.</div>
                </div>
            ) : (
                <>
                    <div className="stats-row">
                        <div className="stat-card">
                            <div className="stat-val">{records.length}개</div>
                            <div className="stat-label">총 기록</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-val">{streak}일</div>
                            <div className="stat-label">연속 운동 일수</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-val">{Math.round(totalMin / 60) || 0}h</div>
                            <div className="stat-label">총 시간</div>
                        </div>
                    </div>

                    {latestWeight && (
                        <div className="export-card" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                            <div style={{ fontSize: 22 }}>⚖️</div>
                            <div>
                                <div style={{ fontSize: 16, fontWeight: 600, fontFamily: 'DM Mono,monospace' }}>{latestWeight.val}kg</div>
                                <div style={{ fontSize: 11, color: 'var(--text3)' }}>최근 체중 · {latestWeight.date}</div>
                            </div>
                        </div>
                    )}

                    {recentRecs.length > 0 ? (
                        <>
                            <p className="section-label">최근 기록</p>
                            <div className="recent-list">
                                {recentRecs.map((r, i) => (
                                    <div key={i} className="record-card" style={{ cursor: 'default' }}>
                                        <div className="record-head">
                                            <div className="record-sport">
                                                <div className="sport-dot" style={{ background: SPORTS[r.sport]?.color || '#888' }} />
                                                <span className="record-name">{SPORTS[r.sport]?.name || r.sport}</span>
                                            </div>
                                            <span className="record-date">{r.date}</span>
                                        </div>
                                        <div className="record-tags">
                                            {r.duration && <span className="tag">{r.duration}분</span>}
                                            {r.sparring && <span className="tag">스파링</span>}
                                            {r.muscleGroup && <span className="tag">{r.muscleGroup}</span>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="empty-state"><div className="empty-text">아직 기록이 없어요</div></div>
                    )}
                </>
            )}
            {confirmRemove && (
                <div className="confirm-backdrop">
                    <div className="confirm-panel">
                        <div className="confirm-title">친구를 삭제할까요?</div>
                        <div className="confirm-desc">{friend.nickname}님이 친구 목록에서 사라져요.</div>
                        <div className="confirm-actions">
                            <button className="action-btn" onClick={() => setConfirmRemove(false)}>취소</button>
                            <button className="action-btn danger" disabled={removing} onClick={async () => {
                                setRemoving(true);
                                try {
                                    await onRemove(friend.uid);
                                    showToast('친구를 삭제했어요');
                                    setConfirmRemove(false);
                                    onBack();
                                } catch {
                                    showToast('친구 삭제에 실패했어요');
                                } finally {
                                    setRemoving(false);
                                }
                            }}>{removing ? '삭제 중...' : '삭제'}</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default function Friends({
    userId,
    user,
    nickname,
    records: myRecords = [],
    friends,
    friendRequests = [],
    sentFriendRequests = [],
    addFriend,
    removeFriend,
    getFriendData,
    acceptFriendRequest,
    declineFriendRequest,
    cancelFriendRequest,
    showToast,
    onBack,
    showBack = true,
}) {
    const [view, setView] = useState('list');
    const [selectedFriend, setSelectedFriend] = useState(null);
    const [addInput, setAddInput] = useState('');
    const [adding, setAdding] = useState(false);
    const [friendData, setFriendData] = useState({});
    const [loadingFriends, setLoadingFriends] = useState(false);
    const [loadedFriendCount, setLoadedFriendCount] = useState(FRIEND_PREFETCH_LIMIT);
    const previousFriendKey = useRef('');

    const friendUidsKey = friends.map(f => f.uid).join(',');
    useEffect(() => {
        let alive = true;
        if (!friends.length) {
            setFriendData({});
            setLoadingFriends(false);
            previousFriendKey.current = '';
            return undefined;
        }
        const friendListChanged = previousFriendKey.current !== friendUidsKey;
        const currentData = friendListChanged ? {} : friendData;
        const currentLimit = friendListChanged ? FRIEND_PREFETCH_LIMIT : loadedFriendCount;
        if (friendListChanged) {
            previousFriendKey.current = friendUidsKey;
            setFriendData({});
            setLoadedFriendCount(FRIEND_PREFETCH_LIMIT);
        }
        const targets = friends
            .slice(0, currentLimit)
            .filter(f => !currentData[f.uid]);
        if (!targets.length) {
            setLoadingFriends(false);
            return undefined;
        }
        setLoadingFriends(true);
        Promise.all(targets.map(f =>
            getFriendData(f.uid)
                .then(data => ({ uid: f.uid, data, error: !data }))
                .catch(() => ({ uid: f.uid, data: null, error: true }))
        )).then(items => {
            if (!alive) return;
            setFriendData(prev => ({ ...prev, ...Object.fromEntries(items.map(item => [item.uid, item])) }));
            setLoadingFriends(false);
        });
        return () => { alive = false; };
    }, [friendUidsKey, loadedFriendCount, getFriendData]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCopyLink = () => {
        const url = `${window.location.origin}${window.location.pathname}?add=${userId}`;
        navigator.clipboard.writeText(url).then(() => showToast('링크 복사 완료!'));
    };

    const handleAdd = async (id) => {
        const targetId = (id || addInput).trim();
        if (!targetId) return;
        setAdding(true);
        try {
            await addFriend(targetId, friends);
            showToast('친구 요청을 보냈어요!');
            setAddInput('');
        } catch (e) {
            const msgs = { not_found: '존재하지 않는 ID예요', self: '자기 자신은 영원한 나의 친구랍니다.', already: '이미 친구예요', pending: '이미 친구 요청을 보냈어요' };
            showToast(msgs[e.message] || '오류가 발생했어요');
        }
        setAdding(false);
    };

    const handleAcceptRequest = async (request) => {
        try {
            await acceptFriendRequest(request);
            showToast('친구 요청을 수락했어요');
        } catch {
            showToast('요청 처리에 실패했어요');
        }
    };

    const handleDeclineRequest = async (request) => {
        try {
            await declineFriendRequest(request);
            showToast('친구 요청을 거절했어요');
        } catch {
            showToast('요청 처리에 실패했어요');
        }
    };

    const handleCancelRequest = async (request) => {
        try {
            await cancelFriendRequest(request);
            showToast('보낸 친구 요청을 취소했어요');
        } catch {
            showToast('요청 취소에 실패했어요');
        }
    };

    const availableFriends = friends
        .slice(0, loadedFriendCount)
        .map(f => ({ friend: f, data: friendData[f.uid]?.data }))
        .filter(item => item.data);
    const myEntry = {
        friend: { uid: user?.uid, nickname: nickname || '나', userId },
        week: weekCount(myRecords),
        streak: calcStreak(myRecords),
        totalMin: myRecords.reduce((a, r) => a + (+r.duration || 0), 0),
        lastDate: [...myRecords].sort((a, b) => new Date(b.date) - new Date(a.date))[0]?.date,
        isMe: true,
    };
    const leaderboard = [
        myEntry,
        ...availableFriends.map(({ friend, data }) => {
            const records = data.records || [];
            return {
                friend,
                week: weekCount(records),
                streak: calcStreak(records),
                totalMin: records.reduce((a, r) => a + (+r.duration || 0), 0),
                lastDate: [...records].sort((a, b) => new Date(b.date) - new Date(a.date))[0]?.date,
            };
        }),
    ].sort((a, b) => b.week - a.week || b.streak - a.streak);
    const today = toDateStr(new Date());
    const trainedToday = availableFriends.filter(({ data }) => (data.records || []).some(r => r.date === today));

    const myUid = user?.uid;
    const friendUids = new Set(friends.map(f => f.uid));
    const weekStart = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - d.getDay()); return d; })();
    const inThisWeek = date => { const d = new Date(date); d.setHours(0, 0, 0, 0); return d >= weekStart; };

    const jointDates = new Set();
    myRecords.filter(r => inThisWeek(r.date)).forEach(r => {
        (r.buddies || []).forEach(b => { if (friendUids.has(b.uid)) jointDates.add(r.date + ':' + b.uid); });
    });
    availableFriends.forEach(({ friend, data }) => {
        (data.records || []).filter(r => inThisWeek(r.date)).forEach(r => {
            if ((r.buddies || []).some(b => b.uid === myUid)) jointDates.add(r.date + ':' + friend.uid);
        });
    });
    const totalWeek = jointDates.size;

    if (view === 'profile' && selectedFriend) {
        return (
            <FriendProfile
                friend={selectedFriend}
                onBack={() => { setView('manage'); setSelectedFriend(null); }}
                onRemove={removeFriend}
                getFriendData={getFriendData}
                showToast={showToast}
            />
        );
    }

    if (view === 'manage') {
        return (
            <>
                <button className="back-btn" onClick={() => setView('list')}>← 친구 피드</button>

                <p className="section-label">내 프로필 공유</p>
                <div className="export-card">
                    <div className="export-title">친구 추가 링크</div>
                    <div className="export-desc">이 링크를 공유하면 상대방이 앱에서 바로 나를 친구 추가할 수 있어요.</div>
                    <div style={{ fontFamily: 'DM Mono,monospace', fontSize: 11, color: 'var(--text3)', marginBottom: 12, wordBreak: 'break-all', lineHeight: 1.6 }}>
                        {window.location.origin}{window.location.pathname}?add={userId}
                    </div>
                    <button className="tpl-btn primary" style={{ width: '100%', padding: 12 }} onClick={handleCopyLink}>
                        링크 복사
                    </button>
                </div>

                <p className="section-label">ID로 친구 추가</p>
                <div className="export-card">
                    <div className="weight-row">
                        <div className="weight-input-wrap">
                            <input
                                className="form-input"
                                placeholder="상대방 ID 입력 (예: my_id_123)"
                                value={addInput}
                                onChange={e => setAddInput(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleAdd()}
                                autoCapitalize="none"
                            />
                        </div>
                        <button
                            className="tpl-btn primary"
                            style={{ flex: 0, padding: '10px 16px', whiteSpace: 'nowrap' }}
                            onClick={() => handleAdd()}
                            disabled={adding}
                        >{adding ? '···' : '추가'}</button>
                    </div>
                    <div style={{ fontFamily: 'DM Mono,monospace', fontSize: 11, color: 'var(--text3)', wordBreak: 'break-all', lineHeight: 1.6 }}>
                        내 ID @{userId}
                    </div>
                </div>

                {(friendRequests.length > 0 || sentFriendRequests.length > 0) && (
                    <>
                        <p className="section-label">친구 요청</p>
                        <div className="request-list">
                            {friendRequests.map(request => (
                                <div key={request.id} className="friend-request-card">
                                    <div className="request-profile compact">
                                        <div className="friend-avatar">{request.fromNickname?.slice(0, 1) || '?'}</div>
                                        <div>
                                            <div className="request-name">{request.fromNickname || '사용자'}</div>
                                            <div className="request-id">@{request.fromUserId || 'unknown'} · 받은 요청</div>
                                        </div>
                                    </div>
                                    <div className="request-actions">
                                        <button className="tpl-btn" onClick={() => handleDeclineRequest(request)}>거절</button>
                                        <button className="tpl-btn primary" onClick={() => handleAcceptRequest(request)}>수락</button>
                                    </div>
                                </div>
                            ))}
                            {sentFriendRequests.map(request => (
                                <div key={request.id} className="friend-request-card">
                                    <div className="request-profile compact">
                                        <div className="friend-avatar">{request.toNickname?.slice(0, 1) || '?'}</div>
                                        <div>
                                            <div className="request-name">{request.toNickname || '사용자'}</div>
                                            <div className="request-id">@{request.toUserId || 'unknown'} · 보낸 요청</div>
                                        </div>
                                    </div>
                                    <div className="request-actions">
                                        <button className="tpl-btn" onClick={() => handleCancelRequest(request)}>요청 취소</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                <p className="section-label">친구 목록{friends.length > 0 ? ` (${friends.length})` : ''}</p>
                {friends.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-text">아직 친구가 없어요.<br />내 ID를 공유하거나 친구 ID를 입력해보세요.</div>
                    </div>
                ) : (
                    <div className="recent-list">
                        {friends.map((f, i) => (
                            <div key={i} className="record-card" onClick={() => { setSelectedFriend(f); setView('profile'); }}>
                                <div className="record-head">
                                    <div>
                                        <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 2 }}>{f.nickname}</div>
                                        <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'DM Mono,monospace' }}>@{f.userId}</div>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                                        {f.addedAt && (
                                            <span style={{ fontSize: 10, color: 'var(--text3)' }}>
                                                {new Date(f.addedAt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })} 추가
                                            </span>
                                        )}
                                        <span style={{ color: 'var(--text3)', fontSize: 18 }}>›</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div style={{ height: 8 }} />
            </>
        );
    }

    return (
        <>
            {showBack && <button className="back-btn" onClick={onBack}>← 더보기</button>}

            <div className="social-hero">
                <div>
                    <div className="social-title">운동 친구들</div>
                    <div className="social-sub">
                        {friends.length ? `이번 주 함께 ${totalWeek}회 운동했어요` : '친구를 추가하고 서로의 운동을 따라가 보세요'}
                    </div>
                </div>
                <button className="social-share-btn" onClick={() => setView('manage')}>
                    {friends.length ? '관리' : '친구 추가'}
                </button>
            </div>

            {friends.length > 0 && (
                <>
                    <div className="social-stats">
                        <div className="social-stat">
                            <div className="social-stat-val">{friends.length}</div>
                            <div className="social-stat-label">친구</div>
                        </div>
                        <div className="social-stat">
                            <div className="social-stat-val">{trainedToday.length}</div>
                            <div className="social-stat-label">오늘 운동</div>
                        </div>
                        <div className="social-stat">
                            <div className="social-stat-val">{totalWeek}</div>
                            <div className="social-stat-label">이번 주 합계</div>
                        </div>
                    </div>

                    <p className="section-label">이번 주 랭킹</p>
                    {loadingFriends ? (
                        <div className="empty-state"><div className="empty-text">···</div></div>
                    ) : leaderboard.length > 0 ? (
                        <div className="leaderboard">
                            {leaderboard.slice(0, 6).map((item, i) => (
                                <div key={item.friend.uid} className="leader-row"
                                    style={item.isMe ? { background: 'var(--bg3)', borderRadius: 10 } : {}}
                                    onClick={!item.isMe ? () => { setSelectedFriend(item.friend); setView('profile'); } : undefined}>
                                    <div className="rank-num">{i + 1}</div>
                                    <div className="friend-avatar">{item.friend.nickname?.slice(0, 1) || '?'}</div>
                                    <div className="leader-main">
                                        <div className="feed-name">
                                            {item.friend.nickname}
                                            {item.isMe && <span style={{ fontSize: 11, color: 'var(--accent)', marginLeft: 6 }}>나</span>}
                                        </div>
                                        <div className="feed-meta">{item.streak}일 동안 연속 운동 · {Math.round(item.totalMin / 60) || 0}h</div>
                                    </div>
                                    <div className="leader-score">{item.week}회</div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="empty-state"><div className="empty-text">친구 기록을 아직 볼 수 없어요</div></div>
                    )}
                    {loadedFriendCount < friends.length && (
                        <button
                            className="tpl-btn"
                            style={{ width: '100%', padding: 12, marginTop: 10 }}
                            onClick={() => setLoadedFriendCount(c => Math.min(friends.length, c + FRIEND_PREFETCH_LIMIT))}
                            disabled={loadingFriends}
                        >
                            친구 기록 더 불러오기 ({Math.min(loadedFriendCount, friends.length)}/{friends.length})
                        </button>
                    )}

                </>
            )}

            {friends.length === 0 && (
                <div className="empty-state">
                    <div className="empty-text">아직 친구가 없어요.<br />친구를 추가하면 대시보드와 피드가 채워져요.</div>
                    <button className="tpl-btn primary empty-action" onClick={() => setView('manage')}>친구 추가하기</button>
                </div>
            )}

            <div style={{ height: 8 }} />
        </>
    );
}
